import type { SearchSubmitPayload, SearchResultItem, PaginationModel, SortModelItem } from '@ncrb/shared-components';
import type { SearchOriginDestination } from './constants';

export type SearchRestoreTabId = 'policy' | 'employer' | 'usr';

export type SearchRestoreSnapshot = {
  source: 'advanced-search';
  tabId: SearchRestoreTabId;
  submittedValues: SearchSubmitPayload['values'];
  rows: SearchResultItem[];
  rowCount: number;
  rowIdField: string;
  paginationModel: PaginationModel;
  sortModel: SortModelItem[];
  rowGroupingModel: string[];
};

export type SearchRestoreNavigationState = {
  searchTab?: string;
  searchOrigin?: SearchOriginDestination;
  searchRestoreSnapshot?: SearchRestoreSnapshot;
};

type NonSearchRestoreNavigationState = Omit<
  SearchRestoreNavigationState,
  'searchTab' | 'searchOrigin' | 'searchRestoreSnapshot'
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function isSortDirection(value: unknown): value is SortModelItem['sort'] {
  return value === 'asc' || value === 'desc';
}

function isSortModelItem(value: unknown): value is SortModelItem {
  return (
    isRecord(value) &&
    typeof value.field === 'string' &&
    isSortDirection(value.sort)
  );
}

export function isSearchRestoreSnapshot(value: unknown): value is SearchRestoreSnapshot {
  if (!isRecord(value)) {
    return false;
  }

  if (value.source !== 'advanced-search') {
    return false;
  }

  if (value.tabId !== 'policy' && value.tabId !== 'employer' && value.tabId !== 'usr') {
    return false;
  }

  if (!Array.isArray(value.rows)) {
    return false;
  }

  const rowCount = value.rowCount;
  if (typeof rowCount !== 'number') {
    return false;
  }

  if (!Number.isFinite(rowCount) || rowCount < 0) {
    return false;
  }

  if (typeof value.rowIdField !== 'string' || value.rowIdField.trim().length === 0) {
    return false;
  }

  const paginationModel = value.paginationModel;
  if (!isRecord(paginationModel)) {
    return false;
  }

  const page = paginationModel.page;
  const pageSize = paginationModel.pageSize;
  if (typeof page !== 'number' || typeof pageSize !== 'number') {
    return false;
  }

  if (!Number.isInteger(page) || page < 0 || !Number.isInteger(pageSize) || pageSize <= 0) {
    return false;
  }

  if (!Array.isArray(value.sortModel) || !value.sortModel.every(isSortModelItem)) {
    return false;
  }

  if (!Array.isArray(value.rowGroupingModel) || !value.rowGroupingModel.every((f) => typeof f === 'string')) {
    return false;
  }

  if (!isRecord(value.submittedValues)) {
    return false;
  }

  return true;
}

export function getSearchRestoreSnapshotFromLocationState(
  state: unknown
): SearchRestoreSnapshot | null {
  if (!isRecord(state) || !('searchRestoreSnapshot' in state)) {
    return null;
  }

  const snapshot = state.searchRestoreSnapshot;
  return isSearchRestoreSnapshot(snapshot) ? snapshot : null;
}

export function removeSearchRestoreSnapshotFromLocationState(state: unknown): unknown {
  if (!isRecord(state)) {
    return state;
  }

  const { searchRestoreSnapshot: _searchRestoreSnapshot, ...nextState } = state;
  return nextState;
}

export function removeSearchRestoreNavigationStateFromLocationState(
  state: unknown
): NonSearchRestoreNavigationState {
  if (!isRecord(state)) {
    return {};
  }

  const {
    searchTab: _searchTab,
    searchOrigin: _searchOrigin,
    searchRestoreSnapshot: _searchRestoreSnapshot,
    ...nextState
  } = state;

  return nextState;
}

export function getSearchOriginFromLocationState(state: unknown): SearchOriginDestination | null {
  if (!isRecord(state)) {
    return null;
  }

  const searchOrigin = state.searchOrigin;
  if (searchOrigin !== 'combo' && searchOrigin !== 'coverage' && searchOrigin !== 'policyDetails') {
    return null;
  }

  return searchOrigin;
}
